/**
 * The OnMarble launcher — product identity, its maker, and the finite
 * platform destinations (#2328 §3/§4, ROOT ruling 74071 §1/§2, B3 ruling,
 * #2345 ROOT solution design 75517).
 *
 * ── #2345: THE CONSTELLATION BECAME A PRODUCT PANEL ───────────────────────
 * 75517 §2 accepted the mockup as the visual direction and replaced the
 * two-column tile grid with *"one anchored, single-column product panel above
 * the trigger"*: a dark inverse-chrome header carrying the exact lockup, then
 * white/surface action rows with a governed icon, label, description and a
 * trailing route/external affordance. Nothing about WHICH actions exist, who
 * may offer them, or how an unresolved one is treated changed — §4 is explicit
 * that the reference's three rows *"are therefore the expected Console result
 * today; it is not permission to manufacture missing … answers"*.
 *
 * This is a restyle of the one shared launcher. There is no second component,
 * no second action list and no second data source.
 *
 * ── THE CONDITIONAL SLOTS ARE ABSENT, AND THAT IS THE MEASUREMENT ─────────
 * The reference also shows a `v1.0.0.648` pill, a `● Running · 42 modules`
 * footer and an `Open ↗` link. §3 makes all four conditional on a governed
 * source and forbids fixture text. MEASURED on this tree (census, #2345
 * comment 75551) and every one of them is ABSENT:
 *
 *   · version — no product/build version door exists in core. The only
 *     `version` functions are a module-version trigger and the per-module rows
 *     of `core.installed_modules()`. `portal.my_environment_versions()` answers
 *     *"which versions do MY environments run"*, per deployment the customer
 *     owns; picking the row for THIS box needs a host derivation, which ruling
 *     74071 §2 forbids. A `package.json` version is bundle-baked, which §3
 *     forbids by name.
 *   · status — `core.deployment_identity()` carries name/region/instance type
 *     and no status; the health read beside it is BACKUP custody. The control
 *     plane's `last_seen_at` is about other boxes reporting IN.
 *   · module count — `core.installed_modules()` is a real runtime answer, but
 *     it is revoked only from `public, anon`, so `authenticated` holds EXECUTE
 *     and a PORTAL SEAT can call it. Publishing this box's module inventory to
 *     an external principal through a door nobody authorized for that is not a
 *     presentation decision.
 *   · Open — needs a governed public HTTPS OnMarble destination. There is none
 *     (ruling 74071 §2), and two existing ratchets forbid writing one:
 *     `launcher.contract.test.ts` for the console shell and
 *     `apps/portal/src/app.footer.test.ts` for the portal app.
 *
 * So the slots are OMITTED COMPLETELY rather than built as props nobody passes.
 * A prop with no source is dead surface by `conformance/dispositions.test.ts`'s
 * own measurement, and the four owed doors are recorded on the issue instead.
 * `host` is the one exception and it is not this lane's: ruling 74071 §2
 * created it deliberately so the value drops in without reshaping the lockup.
 *
 * ── IT FLOATS; IT IS NOT THE FOOTER COMING BACK ───────────────────────────
 * #2328 removed a full-width reserved row from both hosts. The one obligation
 * that row actually owned — Marble attribution — is now part of THIS contract
 * (*"a portal may hide the whole launcher through its governed mode, but may
 * not present an unattributed or counterfeit variant"*), and the amendment adds
 * the maker beside it. So the mount is `position: fixed` in the trailing bottom
 * corner, reserves no height, no width and no row, and nothing in either shell
 * subtracts it from anything. That is a §11 arm and the stylesheet ratchet
 * asserts it. This component REPLACES `ShellAttribution`, which was B2's
 * one-slice placeholder for exactly this mount point.
 *
 * ── #2406: IN THE CONSOLE THE TRIGGER IS THE WALL'S CAP ───────────────────
 * The owner's leading Organization Detail design ends the right edge in a
 * 56px wall whose top course *"IS the product launcher, so there is no
 * floating logo in the corner"*. The Console therefore mounts this component
 * with `placement="cap"` inside `DrawerShell`'s cap slot; the top bar no
 * longer carries it. The panel seats against the wall, opening to the LEFT of
 * the cap and below the bar row, instead of hanging under a tile. The Portal
 * keeps `bar`: its header mount is untouched and it passes no cap.
 *
 * ── NO HOSTNAME. NONE. (ROOT ruling 74071 §2) ─────────────────────────────
 * The design proposed rendering `window.location.host` on the Console. It was
 * reversed: *"On MBN that would advertise a temporary Console/`sslip.io`
 * routing host, not the customer's Marble identity … If it is absent, render
 * `OnMarble` without a hostname; do not guess and do not show a placeholder."*
 *
 * There is no governed public Marble host setting in this tree, so BOTH hosts
 * render the wordmark alone today. The `host` prop exists so that value drops
 * in without reshaping the lockup, and it is the only way a host string can
 * ever reach this component: nothing here reads `window.location`, and
 * `launcher.contract.test.ts` censuses both apps to prove neither passes one.
 * `core.deployment_identity()` must not be consumed for it either (filed,
 * #1420 comment 74079).
 *
 * ── THE LOCKUP IS ON THE OPENED SURFACE (#2345 §1, AMENDED BY #2406) ──────
 * `[Marble mark] OnMarble by [registered MB mark]`, drawn by ONE function so
 * two copies of a brand lockup cannot drift.
 *
 * #2345 §1 asked for that lockup "in BOTH states". Owner ruling 79074/79075
 * amends it: **attribution is owed on the opened product surface, not repeated
 * in a closed control.** So the collapsed trigger is the registered slash mark
 * alone on its governed dark ground, named and tooltipped `Open OnMarble`, and
 * the expanded cap carries `OnMarble by [MB mark]` exactly once. The visible
 * word `Moonbase` is drawn nowhere, and the version is never appended to the
 * identity line.
 *
 * The INERT variant is the one place the trigger still draws the full lockup,
 * and deliberately: nothing resolved means no panel ever opens, so there is no
 * opened surface for the attribution to live on.
 *
 * ── the three modes are the PORTAL's policy, and the Console has none ──────
 * `hidden` renders `null` — no launcher, no mark, no accessible label, no
 * reserved gap. `brand` renders the lockup with About OnMarble alone. `actions`
 * renders the portal-safe subset that actually resolved. The Console always
 * passes `actions` and there is no setting it could read to do otherwise: a
 * console binding that took a mode from data could be configured away, which §4
 * forbids, and `core.experience`'s tenant rung (50) outranks a module's system
 * rung (20), so the cascade could not have held it.
 *
 * ── a lockup with nothing behind it is CHROME, not a dead button ───────────
 * Where no action resolves — a `brand` portal whose About destination is
 * missing — the lockup renders as a static `<div>` with no button role and no
 * `aria-expanded`. `RecordShell` states the same invariant for the same reason:
 * *"a focusable control announced as a command affordance that does nothing is
 * worse than none"*. It is never a disabled button and never a no-op handler.
 *
 * ── the panel, and why it is still not a `menu` ───────────────────────────
 * §5: *"the rows remain ordinary links/buttons in a labelled group, not a fake
 * ARIA menu."* A `role="menu"` would promise arrow-key menu semantics and a
 * roving tabindex, i.e. exactly NOT a linear Tab walk. So the panel is a
 * labelled group of ordinary buttons and links: Tab moves through them in the
 * order `LAUNCHER_ACTION_ORDER` declares, which is also the DOM order. Open
 * state is on the trigger (`aria-expanded` + `aria-controls`).
 *
 * Focus ENTERS the first row on open and returns to the launcher on close —
 * both stated by §5, and the second one is why Escape restores rather than
 * merely closing. An outside CLICK deliberately does not restore, because the
 * person has already put focus somewhere by clicking; `UserMenu` argues that
 * asymmetry at length and this follows it rather than inventing a second rule.
 *
 * ── phone uses the ESTABLISHED bottom sheet, with the SAME row language ───
 * §5: *"Phone keeps the established accessible bottom sheet; apply the same
 * single-column row visual language there instead of shrinking the desktop
 * popover."* So the sheet is the same header + the same rows, and `Modal` is
 * the one modal surface in the platform (#387) whose `bottom` variant owns the
 * trap, Escape, `aria-hidden` on the page behind and focus restoration —
 * rather than any of it being hand-rolled a second time here.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import {
  ChevronRight, Compass, ExternalLink, Info, LifeBuoy,
  SlidersHorizontal, Sparkles,
} from 'lucide-react'
import { MarbleMark } from '../MarbleMark'
import { MoonbaseMark } from '../MoonbaseMark'
import { Modal } from '../controls/Modal'
import { cx } from '../controls/cx'
import { useShellCompact } from './breakpoints'
import {
  resolveLauncherActions,
  type LauncherIconKind, type ResolvedLauncherAction,
} from './launcherActions'
import s from './OnMarbleLauncher.module.css'

/** The governed Portal policy value (#2328 §4). The Console is always `actions`. */
export type OnMarbleLauncherMode = 'hidden' | 'brand' | 'actions'

/**
 * The finite action kinds. Nothing outside this union can be registered, and
 * the vocabulary each one resolves to lives in `launcherActions.ts`.
 */
export type LauncherActionKind =
  | 'discover'
  | 'platform-settings'
  | 'whats-new'
  | 'help'
  | 'about-onmarble'
  | 'contact-moonbase'

/** Which host may offer a kind at all. */
export type LauncherSurface = 'console' | 'portal'

/**
 * A registered action as a HOST offers it.
 *
 * `onSelect` and `href` are both optional and exactly one of them is read, by
 * the kind's own declared destination. Absence is how a host says *"this kind
 * exists and nothing resolved it"*, which is the omission case.
 *
 * There is deliberately no `label`, no `icon` and no `url` for a route kind: a
 * host that could pass those could put anything on the launcher, which is the
 * arbitrary tile the brand amendment refuses. #2345 §2 restates it for the
 * glyph specifically — *"action icons become part of `LAUNCHER_ACTIONS`, not
 * host props"* — and the shape below is the whole of the refusal.
 */
export interface LauncherAction {
  kind: LauncherActionKind
  /** For a `route` kind: what to do. Absent ⇒ unresolved ⇒ omitted. */
  onSelect?: (() => void) | null
  /** For an `external` kind: where to go. Absent or not HTTPS ⇒ omitted. */
  href?: string | null
}

/** The product's own name in the lockup. A constant, never deployment data. */
export const ONMARBLE_WORDMARK = 'OnMarble'
/** The maker name remains available to the full About surface. */
export const MOONBASE_MAKER = 'Moonbase'
/**
 * The trigger's accessible name and tooltip, verbatim (#2406 owner ruling
 * 79074/79075). It was `Open OnMarble by Moonbase`.
 *
 * The amendment moves ATTRIBUTION to the opened product surface: the closed
 * trigger is the registered slash mark alone, so the maker is not announced by
 * a control that does not show it, and `by Moonbase` appears nowhere — the
 * opened cap carries `OnMarble by [registered MB mark]` exactly once instead.
 */
export const LAUNCHER_LABEL = 'Open OnMarble'
/** The opened surface names the PRODUCT; the trigger names the ACT. */
export const LAUNCHER_SURFACE_LABEL = ONMARBLE_WORDMARK

/**
 * Where the trigger lives (#2406).
 *
 * `bar` is a tile inside a host's own header row — the Portal's mount, which
 * is unchanged. `cap` is the Console's: the leading Organization Detail design
 * makes the right-edge wall's top cap THE launcher (*"the dock's cap IS the
 * product launcher, so there is no floating logo in the corner"*), so the
 * trigger fills that cap and the panel seats against the wall, opening to its
 * left, rather than hanging below a tile in the bar.
 *
 * Only the PLACEMENT differs. The rows, the lockup, the modes, the governed
 * refusals and the phone sheet are one implementation either way.
 */
export type OnMarbleLauncherPlacement = 'bar' | 'cap'

export interface OnMarbleLauncherProps {
  /** Which host this is. Decides which kinds may be offered at all. */
  surface: LauncherSurface
  /** `bar` (default) or the dock's `cap`. See `OnMarbleLauncherPlacement`. */
  placement?: OnMarbleLauncherPlacement
  /** The governed mode. `hidden` renders nothing whatsoever. */
  mode: OnMarbleLauncherMode
  /** The registered actions this host resolved. Unresolved ones are omitted. */
  actions?: readonly LauncherAction[]
  /**
   * The governed PUBLIC Marble host, when one exists.
   *
   * Nothing in this tree supplies it and neither host passes it (ruling §2):
   * the setting is core-shaped and lands after #2323's rung. Absent renders the
   * wordmark alone — never a placeholder, never a guess, and never
   * `window.location.host`.
   */
  host?: string | null
  className?: string
}

/**
 * THE EXACT LOCKUP (#2345 §1), drawn once and used in both states.
 *
 * `[Marble mark] OnMarble by [registered MB mark]`. The word `Moonbase` is not
 * in it; the maker is its registered asset. `aria-hidden` on the maker half
 * because the control's own accessible name already carries both identities,
 * and announcing "Moonbase" twice is noise.
 */
function Lockup({ host, size }: { host: string | null; size: 'trigger' | 'header' }) {
  const header = size === 'header'
  return (
    /* `data-lockup` is the seam a browser spec locates this by. It used to be
       found through the trigger's accessible name, which ruling 79074/79075
       then changed — and the spec went on asserting the old name at nine
       profiles for a control that no longer carries it. An attribute says what
       the element IS, and survives the next wording. */
    <span className={cx(s.lockup, header && s.lockupHeader)} data-lockup="onmarble">
      <MarbleMark size={header ? 22 : 18} className={s.marble} />
      <span className={s.names}>
        <span className={s.product}>
          {ONMARBLE_WORDMARK}
          {/* The governed public host, when one exists. It does not today, and
              an absent one renders NOTHING rather than a placeholder. */}
          {host ? <span className={s.host}>{host}</span> : null}
        </span>
        {/* The maker half: visibly secondary, legible, and the REGISTERED mark
            (ruling §1) — never typed initials and never a one-off SVG. */}
        <span className={s.maker} aria-hidden="true" data-lockup-maker="">
          <span className={s.makerWord}>by</span>
          <MoonbaseMark size={header ? 15 : 13} className={s.mb} />
        </span>
      </span>
    </span>
  )
}

/**
 * The governed glyph for one action kind.
 *
 * Exhaustive over `LauncherIconKind` rather than a lookup with a fallback: a
 * fallback glyph is how a vocabulary silently grows a seventh member. The
 * registry decides WHICH glyph an action carries; this decides only how each
 * one is drawn, with the `aria-hidden`/`focusable` pair every decorative mark
 * in this kit carries — the row's label is the accessible name, and an icon
 * never replaces text here (`EntityIcon` states the same rule).
 */
function LauncherIcon({ icon }: { icon: LauncherIconKind }) {
  const props = { size: 18, 'aria-hidden': true, focusable: 'false' } as const
  switch (icon) {
    case 'compass': return <Compass {...props} />
    case 'sliders': return <SlidersHorizontal {...props} />
    case 'sparkles': return <Sparkles {...props} />
    case 'life-buoy': return <LifeBuoy {...props} />
    case 'info': return <Info {...props} />
    case 'maker-mark': return <MoonbaseMark size={18} />
  }
}

export function OnMarbleLauncher({
  surface, placement = 'bar', mode, actions = [], host = null, className,
}: OnMarbleLauncherProps) {
  const cap = placement === 'cap'
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const mountRef = useRef<HTMLDivElement>(null)
  const rowsRef = useRef<HTMLDivElement>(null)
  const compact = useShellCompact()

  /* `hidden` is answered before anything else is computed, and the hooks above
     it are why this is a variable rather than an early return: React's rules of
     hooks do not care that the answer is constant for the life of the mount. */
  const hidden = mode === 'hidden'
  const resolved = hidden ? [] : resolveLauncherActions(actions, surface, mode)

  /* Escape and an outside click both close. Escape ALSO restores focus to the
     trigger, because the person is still driving with the keyboard and §5 says
     Escape "closes and restores focus to the trigger". The compact branch does
     not register either: `Modal` owns Escape, the outside click and the
     restoration there, and two handlers for one gesture closes a sheet twice. */
  useEffect(() => {
    if (!open || compact) return
    const onDoc = (e: MouseEvent) => {
      if (mountRef.current && !mountRef.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      setOpen(false)
      triggerRef.current?.focus()
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, compact])

  /* Focus ENTERS the first row (§5). Layout effect, not an effect, so it runs
     before paint and a keyboard user never sees the panel with focus still
     behind it. The compact branch is the Modal's own `initialFocus`. */
  useLayoutEffect(() => {
    if (!open || compact) return
    const first = rowsRef.current?.querySelector<HTMLElement>('[data-launcher-tile]')
    first?.focus()
  }, [open, compact])

  if (hidden) return null

  const panelId = 'onmarble-constellation'
  const lockup = <Lockup host={host} size="trigger" />

  /* Nothing resolved ⇒ the lockup is chrome. No button, no `aria-expanded`, no
     handler: see the header. The attribution obligation is still met, which is
     the point — it is the ACTIONS that are absent, not the identity. */
  if (resolved.length === 0) {
    return (
      <div ref={mountRef} className={cx(s.mount, className)} data-testid="onmarble-launcher">
        <div className={cx(s.trigger, s.inert)} data-surface={surface} data-mode={mode}>
          {lockup}
        </div>
      </div>
    )
  }

  /* The panel's two parts, shared by the popover and the sheet so §5's "the
     same single-column row visual language" is one definition rather than a
     resemblance between two. */
  const head = (
    <div className={s.panelHead} data-testid="onmarble-launcher-head">
      <Lockup host={host} size="header" />
    </div>
  )
  const rows = (
    <div ref={rowsRef} className={s.rows} data-testid="onmarble-launcher-rows">
      {resolved.map((action) => (
        <Row key={action.kind} action={action} onDone={() => setOpen(false)} />
      ))}
    </div>
  )

  return (
    <div ref={mountRef} className={cx(s.mount, cap && s.mountCap, className)}
      data-testid="onmarble-launcher" data-placement={placement}>
      {/* DOM order is trigger THEN panel — the reading order a disclosure
          implies, and the order `aria-controls` describes. Since #2406 §5.3
          moved the mount into the shell's top bar the panel opens BELOW the
          trigger, so the painted order and the DOM order finally agree; the
          `column-reverse` that used to reconcile them for a bottom-anchored
          control is gone. Tab still walks the trigger and then the rows. */}
      <button
        ref={triggerRef}
        type="button"
        /* The cap is the wall's own top course, not a tile sitting in it: no
           dark ground, no radius, no lift — the leading design draws it in the
           slab material with the registered mark alone. */
        className={cap ? s.cap : cx(s.trigger, s.markOnly)}
        data-surface={surface}
        data-mode={mode}
        data-testid="onmarble-launcher-trigger"
        aria-label={LAUNCHER_LABEL}
        title={LAUNCHER_LABEL}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => setOpen((v) => !v)}
      >
        {/* ── THE CLOSED TRIGGER IS THE MARK ALONE (owner ruling 79074) ─────
            It used to draw the full lockup, because #2345 §1 asked for "the
            exact lockup … used in BOTH states". The amendment supersedes that:
            attribution is owed on the OPENED product surface, not repeated in
            a closed control. So the trigger is the registered slash on its
            governed dark ground, and it is not an unexplained decorative mark
            — `aria-label` and `title` both say `Open OnMarble`.
            The maker mark does not appear here at all: a mark with no room for
            the wording beside it is how a lockup becomes a counterfeit. */}
        <MarbleMark size={18} className={s.marble} />
        {/* NO CHEVRON — owner, 2026-09-22: "Remove the arrow. Just the logo
            please." The trigger is the mark and nothing else. What the chevron
            said — that this opens something, and whether it is open — is on
            `aria-expanded` and `aria-controls` for assistive technology, and
            for the eye the panel itself is the answer. A glyph beside the mark
            also had to be kept pointing the right way as the mount moved (it
            shipped inverted once), which is a maintenance cost for a mark that
            is better alone. */}
      </button>
      {open && !compact
        ? (
          <div
            id={panelId}
            className={cx(s.panel, cap && s.panelCap)}
            role="group"
            aria-label={LAUNCHER_SURFACE_LABEL}
            data-testid="onmarble-constellation"
          >
            {head}
            {rows}
          </div>
          )
        : null}
      {open && compact
        ? (
          <Modal
            label={LAUNCHER_SURFACE_LABEL}
            variant="bottom"
            testid="onmarble-constellation"
            restoreFocusTo={triggerRef}
            onClose={() => setOpen(false)}
          >
            <div className={s.sheet} role="group" aria-label={LAUNCHER_SURFACE_LABEL}>
              {head}
              {rows}
            </div>
          </Modal>
          )
        : null}
    </div>
  )
}

/**
 * One action row.
 *
 * An `external` destination is an `<a>` and a `route` destination is a
 * `<button>`, because they are different acts and a person's browser treats
 * them differently — middle-click, copy-link and the status bar all work on the
 * first and none of them make sense on the second. `rel="noreferrer"` carries
 * `noopener` implicitly and is the value the majority of this tree already
 * uses; the amendment's *"safe external-link behavior"* is that plus the HTTPS
 * parse in `isSafeExternalDestination`, which has already run by the time a row
 * exists at all.
 *
 * The trailing affordance says WHICH of the two this is before it is clicked:
 * a chevron for a route that stays in the product, the external-link glyph for
 * one that leaves it. Both are `aria-hidden` — a link already announces itself
 * as a link, and `target="_blank"` is not a fact the glyph is inventing.
 */
function Row({ action, onDone }: { action: ResolvedLauncherAction; onDone: () => void }) {
  const { spec } = action
  const body = (
    <>
      <span className={s.rowIcon} aria-hidden="true">
        <LauncherIcon icon={spec.icon} />
      </span>
      <span className={s.rowText}>
        <span className={s.rowLabel}>{spec.label}</span>
        <span className={s.rowDesc}>{spec.description}</span>
      </span>
      <span className={s.rowGo} aria-hidden="true">
        {spec.destination === 'external'
          ? <ExternalLink size={15} />
          : <ChevronRight size={17} />}
      </span>
    </>
  )
  if (spec.destination === 'external') {
    return (
      <a
        data-launcher-tile
        data-kind={action.kind}
        className={s.row}
        href={action.href ?? undefined}
        target="_blank"
        rel="noreferrer"
        onClick={onDone}
      >
        {body}
      </a>
    )
  }
  return (
    <button
      data-launcher-tile
      data-kind={action.kind}
      type="button"
      className={s.row}
      onClick={() => { onDone(); action.onSelect?.() }}
    >
      {body}
    </button>
  )
}
